import { 
    GroupDTO, 
    CreateGroupRequest, 
    AddPersonToGroupResponse 
} from 'types/Group';
import { GroupTransaction } from 'types/GroupTransaction';
import { ApiResponse } from 'types/ApiResponse';
import { apiGet, apiPost, apiPut, apiDelete } from 'utils/apiClient';
import { invalidateProfileStatsCache } from 'lib/profileStatsCache';

// -----------------------------------------------------------------------------
//   Service calls
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
async function getGroupsFromUser(username: string): Promise<GroupDTO[]>
{
    const result = await apiGet<GroupDTO[]>(`/groups/${username}`);
    if (process.env.NODE_ENV !== 'production') {
        console.log(result);
    }
    return result;
}

// -----------------------------------------------------------------------------
// Simple cooldown mechanism using a WeakMap or module-scoped variable
let addPersonToGroupLastCallTime = 0;

async function addPersonToGroup(groupName: string, name: string, token: string): Promise<AddPersonToGroupResponse> {
    const COOLDOWN = 3000;

    if (addPersonToGroupLastCallTime && (Date.now() - addPersonToGroupLastCallTime < COOLDOWN)) {
        return { error: `Please wait a moment before trying again.` };
    }
    addPersonToGroupLastCallTime = Date.now();

    try {
        return await apiPost<AddPersonToGroupResponse>(
            `/groups/${groupName}/people`,
            { groupName, name, token },
        );
    } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
    }
}

// -----------------------------------------------------------------------------
async function addTransactionToGroup(
    groupId: string,
    transaction: GroupTransaction,
    language?: string
): Promise<GroupDTO> {
    const result = await apiPost<GroupDTO>(
        `/groups/${groupId}/transactions`,
        { Transaction: transaction, language: language ?? undefined }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function updateTransactionFromGroup(
    groupId: string,
    transaction: GroupTransaction,
    language?: string
): Promise<GroupDTO> {
    const result = await apiPut<GroupDTO>(
        `/groups/${groupId}/transactions`,
        { Transaction: transaction, language: language ?? undefined }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function deleteTransactionFromGroup(groupId: string, transaction: GroupTransaction): Promise<{ message?: string; group?: GroupDTO }>
{
    const result = await apiDelete<{ message?: string; group?: GroupDTO }>(
        `/groups/${groupId}/transactions`,
        { Transaction: transaction }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function getAllTransactionsWithUser(username: string): Promise<GroupTransaction[]>
{
    const result = await apiGet<GroupTransaction[]>(`/groups/${username}/transactions`);
    if (process.env.NODE_ENV !== 'production') {
        console.log(result);
    }
    return result;
}

// -----------------------------------------------------------------------------
async function createGroup(group: CreateGroupRequest): Promise<ApiResponse<GroupDTO>>
{
    const output = await apiPost<ApiResponse<GroupDTO>>(
        `/groups`,
        { GroupData: group }
    );
    invalidateProfileStatsCache(group.creator);
    
    if (process.env.NODE_ENV !== 'production') {
        console.log(output);
    }
    return output;
}

// -----------------------------------------------------------------------------
async function updateGroup(groupId: string, updateData: { name?: string; description?: string; simplifyDebt?: boolean }): Promise<ApiResponse<GroupDTO>>
{
    const result = await apiPut<ApiResponse<GroupDTO>>(
        `/groups/${groupId}`,
        updateData
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function removePersonFromGroup(groupId: string, memberName: string): Promise<ApiResponse<GroupDTO>>
{
    const result = await apiDelete<ApiResponse<GroupDTO>>(
        `/groups/${groupId}/people`,
        { memberName }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function addMockUserToGroup(groupId: string, mockUserName: string): Promise<ApiResponse<GroupDTO>>
{
    const result = await apiPost<ApiResponse<GroupDTO>>(
        `/groups/${groupId}/mock-users`,
        { mockUserName }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function deleteGroup(groupId: string): Promise<ApiResponse<{ success: boolean }>>
{
    const result = await apiDelete<ApiResponse<{ success: boolean }>>(
        `/groups/${groupId}`
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function acceptInvite(inviteToken: string): Promise<ApiResponse<GroupDTO> & { message?: string }> {
    const result = await apiPost<ApiResponse<GroupDTO> & { message?: string }>(
        `/groups/accept-invite`,
        { inviteToken }
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function confirmSettlement(
    groupId: string,
    transactionId: string,
    destinationLiquidityPoolId?: string,
): Promise<GroupDTO> {
    const result = await apiPost<GroupDTO>(
        `/groups/${groupId}/transactions/${transactionId}/confirm-settlement`,
        destinationLiquidityPoolId ? { destinationLiquidityPoolId } : {},
    );
    invalidateProfileStatsCache();
    return result;
}

// -----------------------------------------------------------------------------
async function rejectSettlement(groupId: string, transactionId: string): Promise<GroupDTO> {
    const result = await apiPost<GroupDTO>(
        `/groups/${groupId}/transactions/${transactionId}/reject-settlement`,
        {},
    );
    invalidateProfileStatsCache();
    return result;
}

export { 
    getGroupsFromUser,
    getAllTransactionsWithUser,
    addPersonToGroup,
    addMockUserToGroup,
    acceptInvite,
    addTransactionToGroup,
    deleteTransactionFromGroup,
    updateTransactionFromGroup,
    createGroup,
    updateGroup,
    removePersonFromGroup,
    deleteGroup,
    confirmSettlement,
    rejectSettlement,
}
